import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import { useProperties } from '@/hooks/api/useProperties';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface Property {
  id: string;
  name: string;
  code?: string;
  address?: string;
}

/**
 * Sentinel stored in the existing `pms_selected_property_id` key to mark
 * "portfolio mode" (all accessible properties rolled up), as opposed to a real
 * property id. Chosen so it can never collide with a UUID.
 */
export const ALL_PROPERTIES = '__ALL__' as const;

export type PropertyMode = 'single' | 'portfolio';

interface PropertyContextValue {
  properties: Property[];
  /** The active property in single mode; `null` in portfolio mode. */
  selectedProperty: Property | null;
  /** `'single'` (a selected property) or `'portfolio'` (the __ALL__ sentinel). */
  mode: PropertyMode;
  /** Select a single property — sets mode='single' and persists its id. */
  selectProperty: (property: Property) => void;
  /** Enter portfolio mode — sets mode='portfolio', clears selectedProperty. */
  selectPortfolio: () => void;
  clearProperty: () => void;
  isLoading: boolean;
  isError: boolean;
}

// ─── Context ──────────────────────────────────────────────────────────────────

const PropertyContext = createContext<PropertyContextValue | null>(null);

const STORAGE_KEY = 'pms_selected_property_id';

// ─── Provider ─────────────────────────────────────────────────────────────────

export function PropertyProvider({ children }: { children: ReactNode }) {
  const { data: propertiesData, isLoading, isError } = useProperties();

  const properties: Property[] = useMemo(() => {
    const raw = propertiesData as { data?: Property[] } | undefined;
    return raw?.data ?? [];
  }, [propertiesData]);

  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);

  // Portfolio mode is derivable synchronously from the sentinel — it doesn't
  // need the properties list to resolve (unlike a single-property restore).
  const [mode, setMode] = useState<PropertyMode>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === ALL_PROPERTIES ? 'portfolio' : 'single';
    } catch {
      return 'single';
    }
  });

  // Restore a single-property selection from localStorage on mount. Portfolio
  // mode (the __ALL__ sentinel) intentionally leaves selectedProperty null.
  useEffect(() => {
    if (properties.length === 0) return;

    // Already selected (e.g. user just picked one) — don't override
    if (selectedProperty) return;

    // In portfolio mode there is no single property to restore.
    if (mode === 'portfolio') return;

    const savedId = localStorage.getItem(STORAGE_KEY);
    if (savedId && savedId !== ALL_PROPERTIES) {
      const found = properties.find((p) => p.id === savedId);
      if (found) {
        setSelectedProperty(found);
      }
    }
  }, [properties, selectedProperty, mode]);

  const selectProperty = useCallback((property: Property) => {
    setSelectedProperty(property);
    setMode('single');
    localStorage.setItem(STORAGE_KEY, property.id);
  }, []);

  const selectPortfolio = useCallback(() => {
    setSelectedProperty(null);
    setMode('portfolio');
    localStorage.setItem(STORAGE_KEY, ALL_PROPERTIES);
  }, []);

  const clearProperty = useCallback(() => {
    setSelectedProperty(null);
    setMode('single');
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  const value: PropertyContextValue = useMemo(
    () => ({
      properties,
      selectedProperty,
      mode,
      selectProperty,
      selectPortfolio,
      clearProperty,
      isLoading,
      isError,
    }),
    [properties, selectedProperty, mode, selectProperty, selectPortfolio, clearProperty, isLoading, isError],
  );

  return (
    <PropertyContext.Provider value={value}>
      {children}
    </PropertyContext.Provider>
  );
}

// ─── Hooks ────────────────────────────────────────────────────────────────────

export function usePropertyContext(): PropertyContextValue {
  const ctx = useContext(PropertyContext);
  if (!ctx) {
    throw new Error('usePropertyContext must be used within <PropertyProvider>');
  }
  return ctx;
}

/** Convenience hook: returns the selected property ID or empty string. */
export function useSelectedPropertyId(): string {
  const { selectedProperty } = usePropertyContext();
  return selectedProperty?.id ?? '';
}

/** Convenience hook: returns the current view mode ('single' | 'portfolio'). */
export function usePropertyMode(): PropertyMode {
  const { mode } = usePropertyContext();
  return mode;
}

/** Convenience hook: true when the app is in portfolio (all-properties) mode. */
export function useIsPortfolio(): boolean {
  const { mode } = usePropertyContext();
  return mode === 'portfolio';
}
